/**
 * How the Hub writes dates. One place, because "the entire program" has to agree.
 *
 * dd/mm/yyyy, and 24-hour time — South African convention, and unambiguous either way, which
 * `toLocaleDateString()` was not: it followed whatever locale the browser happened to be set
 * to, so the same date read differently on two machines in the same office.
 */

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * A date-only string is NOT put through `new Date()`.
 *
 * `new Date('2026-08-27')` is parsed as UTC midnight, so anywhere west of Greenwich it prints
 * as the 26th. Splitting the string keeps the day somebody typed.
 */
function parts(value: string): { day: number; month: number; year: number } | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return { year: Number(dateOnly[1]), month: Number(dateOnly[2]), day: Number(dateOnly[3]) };
  }

  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return null;

  // A timestamp is shown where the reader is, which is what a local time means.
  return { year: when.getFullYear(), month: when.getMonth() + 1, day: when.getDate() };
}

/** dd/mm/yyyy. An em dash for nothing, so a column never has a blank cell of unclear meaning. */
export function formatDate(value: string | null | undefined, whenEmpty = '—'): string {
  if (!value) return whenEmpty;
  const p = parts(value);
  return p ? `${pad(p.day)}/${pad(p.month)}/${p.year}` : whenEmpty;
}

/** HH:mm, 24-hour. Only meaningful for a timestamp; a date-only value has no time. */
export function formatTime(value: string | null | undefined, whenEmpty = '—'): string {
  if (!value) return whenEmpty;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return whenEmpty;
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/** dd/mm/yyyy HH:mm — what a logged event wants: the day, and the time to the minute. */
export function formatDateTime(value: string | null | undefined, whenEmpty = '—'): string {
  if (!value) return whenEmpty;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return whenEmpty;
  return `${formatDate(value, whenEmpty)} ${formatTime(value, '')}`.trim();
}

/**
 * Today's events read as a time alone, older ones carry their date.
 *
 * For a feed of activity, "14:32" says what "04/09/2026 14:32" says while being far quicker
 * to scan — and the moment it is not today, the date matters again.
 */
export function formatWhen(value: string | null | undefined, whenEmpty = '—'): string {
  if (!value) return whenEmpty;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return whenEmpty;

  return when.toDateString() === new Date().toDateString() ? formatTime(value, whenEmpty) : formatDateTime(value, whenEmpty);
}

/**
 * A weekday beside the numbers — "Mon 07/09/2026".
 *
 * For a heading or a day column, where knowing it is a Monday matters as much as the date.
 * The weekday stays a word; the date itself is dd/mm as everywhere else.
 */
export function formatDayLabel(value: string | null | undefined, opts: { year?: boolean } = {}): string {
  if (!value) return '—';
  const p = parts(value);
  if (!p) return '—';

  const weekday = new Date(p.year, p.month - 1, p.day).toLocaleDateString('en-GB', { weekday: 'short' });
  return `${weekday} ${pad(p.day)}/${pad(p.month)}${opts.year ? `/${p.year}` : ''}`;
}

/** Today, as the yyyy-mm-dd an <input type="date"> and the API both expect. */
export function todayValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The first of the month we are in, in the same shape.
 *
 * BUILT FROM LOCAL PARTS, like `todayValue`, and that is the whole reason it exists rather than
 * being written out at each call site. `new Date(y, m, 1).toISOString()` is the obvious way to
 * do this and is wrong everywhere east of Greenwich, every day of the year: the Date is local
 * midnight, `toISOString` renders it in UTC, and in South Africa that is 22:00 the evening
 * before — so "the first of September" comes out as 2026-08-31.
 */
export function monthStartValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
}
